import {
  ControlledAutocompleteSelect,
  ControlledAutocompleteSelectOption,
  Icon,
  LabelSmallRegular,
  Loading
} from '@hm/fundamental'
import {mdiClose} from '@mdi/js'
import {useTranslation} from 'react-i18next'

import {REPORT_FILTERS_WIDGET_NAMESPACE} from '../consts'

import {CollapsibleFilterWrapper} from './CollapsibleFilterWrapper'
import {useCustomPillsRender} from './CustomPillsRender'
import {PillsFilter} from './PillsFilter'

export interface BaseFilterProps {
  name: string
  title: string
  options: ControlledAutocompleteSelectOption[]
  selectedValues: (string | number)[]
  setSelectedValues: (values: string[]) => void
  isCollapsible?: boolean
  useAutocomplete?: boolean
  onBlur?: () => void
  isLoading?: boolean
  displayTitle?: boolean
  /**
   * Always collapse selected values into a single compact "N selected" pill
   * instead of one pill per value. Use for filters whose option labels can be
   * long (e.g. suppliers) so the autocomplete's clear/chevron icons never get
   * pushed outside the field.
   */
  compactSelection?: boolean
  /**
   * Hide the "All" pill for single-select style filters (e.g. bypass dust)
   * where selecting all options is not a meaningful action.
   */
  hideAllOption?: boolean
}
export const BaseFilter = ({
  name,
  title,
  options,
  setSelectedValues,
  selectedValues,
  isCollapsible = true,
  useAutocomplete = false,
  onBlur,
  isLoading = false,
  displayTitle = false,
  compactSelection = false,
  hideAllOption = false
}: BaseFilterProps) => {
  const {t} = useTranslation(REPORT_FILTERS_WIDGET_NAMESPACE)
  const customPillsRender = useCustomPillsRender(options.length, () => setSelectedValues([]))

  const baseFilter = isLoading ? (
    <div className="flex min-h-8 items-center justify-center">
      <Loading size="small" data-cy={`${name}_filter_loader`} />
    </div>
  ) : options.length < 10 && !useAutocomplete ? (
    <PillsFilter
      name={name}
      title={title}
      options={options}
      selectedValues={selectedValues}
      onChange={(values) => setSelectedValues(values as string[])}
      displayTitle={displayTitle}
      hideAllOption={hideAllOption}
    />
  ) : (
    <ControlledAutocompleteSelect
      label={title}
      options={options}
      selectedValues={selectedValues}
      onChange={(values) => setSelectedValues(values as string[])}
      isMultiSelect
      showPills
      onBlur={onBlur}
      renderPills={compactSelection || selectedValues.length > 4 ? customPillsRender : undefined}
    />
  )

  const allSelected = selectedValues.length === options.length
  const selectedText = allSelected
    ? t('filterOptions.allSelected')
    : `${selectedValues.length} ${t('filterOptions.selected')}`

  const rightSlot =
    !isLoading && selectedValues.length > 0 ? (
      <div
        className="flex flex-row items-center gap-2 cursor-pointer"
        onClick={() => setSelectedValues([])}
        data-cy={`${name}_filter_clear`}
      >
        <LabelSmallRegular>{selectedText}</LabelSmallRegular>
        <Icon className="text-gray-500" path={mdiClose} />
      </div>
    ) : undefined

  return (
    <div data-cy={`${name}_filter`}>
      {isCollapsible ? (
        <CollapsibleFilterWrapper title={title} defaultOpen={true} rightSlot={rightSlot}>
          {baseFilter}
        </CollapsibleFilterWrapper>
      ) : (
        baseFilter
      )}
    </div>
  )
}
